import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { COMPOSITE_SIGNAL_DATA, GENERAL_CATALOG, SIGNAL_DATA, SIGNALS_CATALOG, SIGNALS_FUNCTION } from 'src/app/core/config/config.constants';
import { environment } from 'src/environments/environment';
import { isRenderableSignal, SignalApiResponse, SignalCatalog, SignalsApiRequest, Widget } from '../models/widget-models';

@Injectable({
  providedIn: 'root'
})
export class SignalsService {
  
  private baseUrl: string = environment.baseTsOhwUrl;

  constructor(private _httpClient: HttpClient) { }

  getSignalsTrendData(command: SignalsApiRequest): Observable<any> {
    const apiCommand = {
      uidCustomer: command.customerId,
      signals: command.signals,
      fromDate: command.fromDate,
      toDate: command.toDate,
      timeZone: "utc",
      aggregate: command.aggregateData ?? true,
      uidVehicles: command.machineIds
    };
    return this._httpClient.post(`${this.baseUrl}${SIGNAL_DATA}`, apiCommand);
  }

  getCompositeSignalTrendData(command: SignalsApiRequest): Observable<any> {
    const apiCommand = {
      uidCustomer: command.customerId,
      signals: command.signals,
      fromDate: command.datesRange[0],
      toDate: command.datesRange[1],
      timeZone: "utc",
      aggregate: command.aggregateData ?? true,
      uidVehicles: command.machineIds
    };

    return this._httpClient.post(`${this.baseUrl}${COMPOSITE_SIGNAL_DATA}`, apiCommand);
  }

  getCustomerSignalsConfigurationAvailable(userId: string, customerId: string): Observable<any> {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    const storedUserId = JSON.parse(localStorage.getItem('user')!).user.uid;
    return this._httpClient.get(`${this.baseUrl}${GENERAL_CATALOG}?scope=catalog_config&uidUser=${userId ?? storedUserId}&uidCustomer=${customerId}&audience=CUSTOMER`);
  }

  getSignalsAvailable(customerId: string): Observable<any> {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    return this._httpClient.get(`${this.baseUrl}${SIGNALS_CATALOG}?uidCustomer=${customerId}`);
  }

  /**
   * Extracts unique available signals from the provided data, grouping them by their 'scope'.
   * Uniqueness is determined by the combination of 'name' and 'scope' properties.
   * @param data The API response containing 'commonAttributes' and 'modelsAttributes'.
   * @returns An array of objects, each with a 'scope' and its corresponding unique signals.
   */
  extractSignalsCatalog(data: any): {[key: string]: any[]} {
    if (!data) return {};
    const allSignals: any[] = [];
    const seen = new Set<string>();
    // Add all base_signals
    if (Array.isArray(data.commonAttributes)) {
      for (const sig of data.commonAttributes) {
        if (sig.name !== 'vin') { // Exclude 'vin' signal
          const uniqueKey = `${sig.name}__${sig.scope}`;
          if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            allSignals.push(sig);
          }
        }
      }
    } else if (typeof data.commonAttributes === 'object') {
      for (const key in data.commonAttributes) {
        const sig = data.commonAttributes[key];
        if (sig.name !== 'vin') { // Exclude 'vin' signal
          const uniqueKey = `${sig.name}__${sig.scope}`;
          if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            allSignals.push(sig);
          }
        }        
      }
    } 
    // Add unique signals from all model_signals[].signals
    if (Array.isArray(data.modelsAttributes)) {
      for (const model of data.modelsAttributes) {
        if (Array.isArray(model.attributes)) {
          for (const sig of model.attributes) {
            const uniqueKey = `${sig.name}__${sig.scope}`;
            if (!seen.has(uniqueKey)) {
              seen.add(uniqueKey);
              allSignals.push(sig);
            }
          }
        }
      }
    }
    // Group by scope
    const groupedByScope: { [scope: string]: any[] } = {};
    for (const sig of allSignals) {
      const scope = sig.scope || '';
      if (!groupedByScope[scope]) groupedByScope[scope] = [];
      groupedByScope[scope].push(sig);
    }

    console.log('Extracted Signals Catalog:', groupedByScope);
    return groupedByScope;
  }
  
  /**
   * Groups an array of objects by their `signal` property.
   *
   * @param data - The array of objects to group. Each object is expected to have a `signal` property.
   * @returns An object where each key is a unique `signal` value and the corresponding value is an array of objects with that `signal`.
   */
    /**
     * Groups the new API data structure by signal name.
     * @param apiData Array of objects with 'vin' and 'signals' properties.
     * @returns An object where each key is a signal name and the value is an array of all its values (with vin info).
     */
    groupBySignal(apiData: any[]): { [signal: string]: any[] } {
      if (!Array.isArray(apiData)) return {};

      const grouped: { [signal: string]: any[] } = {};

      apiData.forEach(item => {
        if (Array.isArray(item.signals)) {
          item.signals.forEach(sigObj => {
            if (Array.isArray(sigObj.values)) {
              if (!grouped[sigObj.signal]) grouped[sigObj.signal] = [];
              grouped[sigObj.signal] = grouped[sigObj.signal].concat(
                sigObj.values.map(v => ({
                  ...v,
                  vin: item.vin,
                  signal: sigObj.signal
                }))
              );
            }
          });
        }
      });

      return grouped;
    }

  groupConsumption(data: any[]): { [signal: string]: number } {
    return data?.reduce((acc: { [signal: string]: number }, curr: any) => {
      const key = curr.signal;
      if (!acc[key]) acc[key] = 0;
      acc[key] += Number(curr.value) || 0;
      return acc;
    }, {});
  }

  getUniqueSignals(widgets: Widget[], isComposite: boolean = false): string[] {
    return Array.from(
      new Set(
        widgets.map(widget => {
          const signalType = isComposite 
            ? (widget.config?.specific?.signalType?.filter(s => s.scope.includes('composite')) as SignalCatalog)
            : (widget.config?.specific?.signalType?.filter(s => !s.scope.includes('composite')) as SignalCatalog);
          if (Array.isArray(signalType)) {
          return signalType.map(sig => sig.name);
          } else if (signalType != null) {
          return [signalType.name];
          }
          return [];
        })
        .reduce((acc, val) => acc.concat(val), [])
      )
    );
  }

  /**
   * Extracts only the expected signals for a widget from a grouped signals object.
   * @param groupedData The object with signals as keys and arrays as values.
   * @param expectedSignals The array of signal names to extract.
   * @returns An object with only the expected signals as keys.
   */
  extractExpectedSignals(
    groupedData: { [signal: string]: any[] },
    expectedSignals: SignalCatalog[]
  ): { [signal: string]: { signal: SignalCatalog, data: SignalApiResponse[] } } {
    if (!groupedData || !expectedSignals) return {};
    // Esclude i segnali disabilitati/eliminati dal catalogo: solo presentazionale, expectedSignals
    // (quindi widget.config.specific.signalType) non viene toccato.
    const renderableSignals = expectedSignals.filter(isRenderableSignal);
    return renderableSignals.reduce((acc, signal) => {
      const baseSignalName = signal.name;
      if (groupedData[baseSignalName]) {
        acc[baseSignalName] = {
          signal,
          data: groupedData[baseSignalName]
            .slice()
            .sort((a, b) => new Date(a.tstamp).getTime() - new Date(b.tstamp).getTime())
        };
      }
      return acc;
    }, {} as { [signal: string]: { signal: SignalCatalog, data: any[] } });
  }

  mergeConfigWithCatalogSignals(configSignals: { [scope: string]: SignalCatalog[] }, catalogSignals: { [scope: string]: SignalCatalog[] }): { [scope: string]: SignalCatalog[] } {
    const merged: { [scope: string]: SignalCatalog[] } = {};
    for (const scope in catalogSignals) {
      merged[scope] = catalogSignals[scope].map(catalogSig => {
        const configSig = configSignals[scope]?.find(s => s.name === catalogSig.name); 
        if(configSig) {
          return {
            ...configSig,
            uid: catalogSig.uid,
          };
        } else {
          return catalogSig;
        }
      });
    }
    
    
    /* for (const scope in configSignals) {
      merged[scope] = configSignals[scope].map(configSig => {
        const catalogSig = catalogSignals[scope]?.find(s => s.name === configSig.name);
        if (catalogSig) {
          return {
            ...configSig,
            uid: catalogSig.uid,
          };
        } else {
          return configSig;
        }
      });
    } */
    return merged;
  }

  getSignalName(signal: SignalCatalog): string {
    if (!signal) return '';
    if (signal.i18nLabel) {
      const userLang = localStorage.getItem('language') || 'en';
      
      // Try exact match first
      if (signal.i18nLabel[userLang]) {
        return signal.i18nLabel[userLang];
      }
      
      // Find key that contains userLang as substring
      const matchingKey = Object.keys(signal.i18nLabel).find(key =>
        key.toLowerCase().includes(userLang.toLowerCase())
      );
      
      if (matchingKey && signal.i18nLabel[matchingKey]) {
        return signal.i18nLabel[matchingKey];
      }
      
      return signal.label || signal.name;
    }
    return signal.label || signal.name;
  }

}